export const W7_SERVICE_WORKER_PATH = "/sw-w7-anchor-v1.js";

export interface W7ControllerState {
  readonly enabled: boolean;
  readonly expectedScriptUrl: string;
}

interface W7ServiceWorkerController {
  readonly scriptURL: string;
}

interface W7ServiceWorkerRegistration {
  update(): Promise<unknown>;
}

interface W7ServiceWorkerContainer {
  readonly controller: W7ServiceWorkerController | null;
  register(
    scriptUrl: string,
    options: { readonly scope: "/"; readonly updateViaCache: "none" },
  ): Promise<W7ServiceWorkerRegistration>;
  addEventListener(type: "controllerchange", listener: () => void): void;
}

interface W7ControllerEnvironment {
  readonly pageOrigin: string;
  readonly serviceWorker: W7ServiceWorkerContainer;
  reload(): void;
}

function exactHttpsOrigin(value: string): string {
  const parsed = new URL(value);
  if (
    parsed.protocol !== "https:" ||
    parsed.origin !== value ||
    parsed.username !== "" ||
    parsed.password !== "" ||
    parsed.port !== "" ||
    parsed.pathname !== "/" ||
    parsed.search !== "" ||
    parsed.hash !== ""
  ) {
    throw new TypeError("configured vendor origin is invalid");
  }
  return value;
}

function browserEnvironment(): W7ControllerEnvironment | undefined {
  if (!("serviceWorker" in navigator)) return undefined;
  return {
    pageOrigin: window.location.origin,
    serviceWorker: navigator.serviceWorker,
    reload: () => window.location.reload(),
  };
}

/**
 * Installs the exact W7-capable worker without treating an installed
 * registration as control of this document. A document that starts under an
 * older controller stays W7-disabled and may reload once only after the exact
 * controller takes over.
 */
export async function prepareW7Controller(
  configuredVendorOrigin: string,
  environment: W7ControllerEnvironment | undefined = browserEnvironment(),
): Promise<W7ControllerState> {
  const origin = exactHttpsOrigin(configuredVendorOrigin);
  const expectedScriptUrl = new URL(W7_SERVICE_WORKER_PATH, origin).href;
  if (environment === undefined || environment.pageOrigin !== origin) {
    return { enabled: false, expectedScriptUrl };
  }

  if (environment.serviceWorker.controller?.scriptURL === expectedScriptUrl) {
    return { enabled: true, expectedScriptUrl };
  }

  let reloadClaimed = false;
  environment.serviceWorker.addEventListener("controllerchange", () => {
    if (
      !reloadClaimed &&
      environment.serviceWorker.controller?.scriptURL === expectedScriptUrl
    ) {
      reloadClaimed = true;
      environment.reload();
    }
  });

  try {
    const registration = await environment.serviceWorker.register(
      expectedScriptUrl,
      { scope: "/", updateViaCache: "none" },
    );
    await registration.update();
  } catch {
    // Registration is an availability input only. This document remains on
    // legacy admission and can retry on a later load.
  }

  return { enabled: false, expectedScriptUrl };
}
